import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Logo } from '../components/shell/Logo'
import { Input, Select } from '../components/ui'
import { GatedButton } from '../components/ui/GatedButton'
import { useToast } from '../contexts/ToastContext'
import { useAuth } from '../contexts/AuthContext'
import { ageFromDob } from '../lib/age'
import { cn } from '../lib/cn'
import { bootstrapAccount, getConsentRecord, registerCircleOfTrust } from '../lib/api/signup'
import { getMe } from '../lib/api/me'
import { openVerificationProvider, startVerificationCheckout } from '../lib/api/verification'
import { CotApiError } from '../lib/api/client'
import { useReturnDestination, withReturnPath } from '../lib/returnPath'
import {
  useSignupWizard,
  VERIFICATION_METHODS,
  type Relationship,
  type Supervision,
} from '../hooks/useSignupWizard'

const REL_OPTIONS: Array<{ value: Relationship; label: string }> = [
  { value: 'parent', label: 'Parent' },
  { value: 'guardian', label: 'Legal guardian' },
  { value: 'foster', label: 'Foster carer' },
  { value: 'kinship', label: 'Kinship carer with parental authority' },
]

const SUP_OPTIONS: Array<{ value: Supervision; label: string }> = [
  { value: 'strict', label: 'Approve everything' },
  { value: 'view', label: 'View only' },
  { value: 'self', label: 'They send their own requests' },
]

type Tone = 'neutral' | 'positive' | 'warn'

function toneBox(tone: Tone): string {
  return tone === 'positive'
    ? 'bg-moss-panel text-moss'
    : tone === 'warn'
      ? 'bg-honey-bg text-honey-fg'
      : 'bg-paper-panel text-ink-muted'
}

function pillClasses(pill: string): string {
  return pill === 'NEVER'
    ? 'bg-honey-chip text-honey-fg'
    : pill === 'RIGHTS'
      ? 'bg-moss-bg text-moss'
      : 'bg-chip text-chip-fg'
}

function downloadText(filename: string, content: string) {
  try {
    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch {
    /* download blocked — the toast in the caller is the fallback */
  }
}

/**
 * Last-resort label for an acknowledgement that arrives with neither a title nor notice text:
 * "signup_data_care" → "Signup Data Care". Keeps a machine key from ever reaching a person.
 */
function humanizeAckKind(kind: string): string {
  return kind
    .split(/[_\-.]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/** A ring-and-fill checkbox matching the design's consent controls. */
function CheckRow({
  checked,
  onToggle,
  children,
}: {
  checked: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={checked}
      className={cn(
        'flex w-full items-start gap-3 rounded-input border p-4 text-left transition-colors',
        checked ? 'border-terra bg-paper-panel' : 'border-line-soft bg-transparent',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'mt-[2px] flex h-5 w-5 flex-none items-center justify-center rounded-full border text-[11px] text-paper-surface',
          checked ? 'border-moss bg-moss' : 'border-line bg-transparent',
        )}
      >
        {checked ? '✓' : ''}
      </span>
      <span className="text-[13px] leading-relaxed text-ink">{children}</span>
    </button>
  )
}

export function SignupPage() {
  const wiz = useSignupWizard()
  const navigate = useNavigate()
  const returnTo = useReturnDestination()
  const { flash } = useToast()
  const { isAuthenticated, isLoading: authLoading, login, user } = useAuth()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const completing = searchParams.get('complete') === '1' && isAuthenticated
  const existing = useQuery({ queryKey: ['community', 'me'], queryFn: getMe, enabled: isAuthenticated, retry: false })

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [guardianPin, setGuardianPin] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [resume, setResume] = useState(false)
  const decidedRef = useRef(false)

  // Resume (R1): a caller who reaches /signup ALREADY authenticated at load has registered but
  // their bootstrap did not land — jump to review and retry bootstrap only (no re-register/login).
  // Decided exactly once, so the mid-flow login during a normal signup never re-triggers it.
  useEffect(() => {
    if (authLoading || decidedRef.current) return
    decidedRef.current = true
    if (isAuthenticated) {
      setResume(true)
      if (!wiz.draft.name && user) { wiz.setName(user.displayName); wiz.setEmail(user.email) }
      if (!completing && wiz.draft.name && wiz.draft.notice) wiz.goToReview()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated])

  useEffect(() => {
    if (!existing.data) return
    if (!completing) { navigate(withReturnPath('/verification', returnTo), { replace: true }); return }
    if (existing.data.verification?.onboardingComplete) { navigate(withReturnPath('/verification', returnTo), { replace: true }); return }
    wiz.bindFamily(existing.data.family.id)
    // The binding is server-owned; it never comes from a return URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.data, completing, navigate, returnTo])

  const step = wiz.step
  const kids = wiz.draft.kids
  const anyUnder13 = kids.some((k) => {
    const a = ageFromDob(k.dob)
    return a !== null && a < 13
  })

  const passwordOk = password.length >= 8 && password === confirm
  // A family account needs a guardian PIN so switching up to an adult is gated (R12); solo has no
  // managed profiles to gate.
  const pinOk = completing || wiz.solo || /^\d{4,8}$/.test(guardianPin)
  const step1Ok = wiz.gates[1] && (resume || passwordOk) && pinOk

  const effectiveGateOk = (s: number): boolean => (s === 1 ? step1Ok : s === 5 ? wiz.gates[5] && pinOk : wiz.gates[s] ?? true)
  const effectiveHint = (s: number): string => {
    if (s === 1 && wiz.gates[1]) {
      if (!resume && !passwordOk) {
        return password.length < 8 ? 'Choose a password of at least 8 characters.' : 'Your passwords do not match.'
      }
      if (!pinOk) return 'Set a 4–8 digit family guardian PIN — it gates switching to an adult.'
    }
    if (s === 5 && !pinOk) return 'Go back to Your account and set a 4–8 digit family guardian PIN.'
    if (s === 5 && !wiz.gates[5]) return 'Go back and review the current agreements, profile details and consent before continuing.'
    return wiz.hints[s] ?? ''
  }

  async function submit() {
    if (submitting) return
    setSubmitting(true)
    try {
      if (completing) {
        if (!existing.data) throw new Error('Your account is still loading. Please try again.')
        wiz.bindFamily(existing.data.family.id)
        navigate(withReturnPath('/verification', returnTo))
        return
      }
      if (!resume) {
        await registerCircleOfTrust({
          legalName: wiz.draft.name,
          email: wiz.draft.email,
          password,
          dateOfBirth: wiz.draft.dob,
          acknowledgements: wiz.ackRequirements,
        })
        await login(wiz.draft.email.trim(), password)
        setResume(true)
      }
      const me = await bootstrapAccount({ ...wiz.buildBootstrapRequest(), guardianPin: guardianPin || undefined })
      wiz.bindFamily(me.family.id)
      queryClient.setQueryData(['community', 'me'], me)
      navigate(withReturnPath('/verification', returnTo))
      // The server creates the hosted session. No child draft travels in this request.
      const checkout = await startVerificationCheckout()
      openVerificationProvider(checkout.url)
    } catch (err) {
      const status = err instanceof CotApiError ? err.status : undefined
      if (status === 409) {
        // Existing adult account still needs an authoritative verification/readiness check.
        navigate(withReturnPath('/verification', returnTo))
        return
      }
      flash(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  function handleNext() {
    if (!effectiveGateOk(step)) {
      flash(effectiveHint(step))
      return
    }
    if (step === 5) {
      void submit()
      return
    }
    wiz.advance()
  }

  function leave() {
    wiz.clearDraft()
    navigate(isAuthenticated ? '/' : withReturnPath('/login', returnTo))
  }

  async function download() {
    try {
      const text = (await getConsentRecord('text')) as string
      downloadText('circle-of-trust-consent-record.txt', text)
      flash('Consent record downloaded.')
    } catch {
      flash('Could not generate your record right now.')
    }
  }

  const consentTitle = wiz.solo ? 'Confirm your accountability.' : 'Your accountability and consent.'
  const consentIntro = wiz.solo
    ? 'Choose the $1/month card payment for your account. After account creation, Stripe processes the payment and we wait for confirmed evidence.'
    : anyUnder13
      ? 'At least one profile is for a child under 13. We require parental consent and a confirmed payment before creating any child profile. You attest that you have authority to consent for each child.'
      : 'We require your consent and a confirmed payment before creating managed profiles. You attest that you have authority to consent for each person.'
  const withdrawNote = 'You can manage or cancel the monthly subscription and separately withdraw consent from Parent verification. Withdrawal stops community participation. Historical records remain; no deletion deadline is promised here.'
  const sigIntro = wiz.solo
    ? 'Type your full legal name exactly as you entered it. This signs your acceptance of the notice above, dated and downloadable.'
    : 'Type your full legal name exactly as you entered it. This is your signature on the consent above, and we keep a dated copy you can download.'

  const stepBars = useMemo(
    () =>
      wiz.labels.map((label, i) => {
        const n = i + 1
        return { label, done: step > n, active: step === n }
      }),
    [wiz.labels, step],
  )

  return (
    <div className="min-h-screen bg-paper-page px-4 py-8">
      <div className="mx-auto w-full max-w-[720px]">
        <div className="mb-6 flex items-center">
          <Logo insetColor="#efe8db" />
          <button
            type="button"
            onClick={leave}
            className="ml-auto font-sans text-[12.5px] font-medium text-terra-deep hover:text-terra-hover"
          >
            Leave signup
          </button>
        </div>

        {/* Progress bars + labels */}
        <div className="mb-7 grid grid-cols-6 gap-2" aria-hidden>
          {stepBars.map((b) => (
            <div key={b.label} className="min-w-0">
              <div
                className={cn(
                  'h-1 rounded-full',
                  b.done ? 'bg-moss' : b.active ? 'bg-ink' : 'bg-line-soft',
                )}
              />
              <div className={cn('mt-2 truncate font-mono text-[9.5px] uppercase tracking-label', b.active ? 'text-ink' : 'text-ink-faint')}>
                {b.label}
              </div>
            </div>
          ))}
        </div>

        <div className="animate-rise rounded-card border border-line-soft bg-paper-surface p-5 shadow-card sm:p-8">
          {step === 1 && (
            <StepAccount
              wiz={wiz}
              password={password}
              confirm={confirm}
              setPassword={setPassword}
              setConfirm={setConfirm}
              guardianPin={guardianPin}
              setGuardianPin={setGuardianPin}
              relOptions={REL_OPTIONS}
              existingAccount={resume}
              needsPin={!completing}
            />
          )}
          {step === 2 && <StepNotice wiz={wiz} />}
          {step === 3 && <StepProfiles wiz={wiz} supOptions={SUP_OPTIONS} onFlash={flash} />}
          {step === 4 && (
            <StepConsent
              wiz={wiz}
              title={consentTitle}
              intro={consentIntro}
              sigIntro={sigIntro}
            />
          )}
          {step === 5 && <StepReview wiz={wiz} withdrawNote={withdrawNote} />}
          {step === 6 && (
            <div className="rounded-card bg-moss-panel p-8 text-center">
              <div className="mb-2 font-serif text-[26px] font-medium text-ink">{wiz.doneTitle}</div>
              <p className="mx-auto mb-6 max-w-[58ch] text-[13.5px] leading-relaxed text-ink-muted">{wiz.doneBody}</p>
              <div className="flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    wiz.clearDraft()
                    navigate('/')
                  }}
                  className="rounded-btn bg-moss px-[18px] py-3 text-[13.5px] font-medium text-paper-surface"
                >
                  Go to my circles
                </button>
                <button
                  type="button"
                  onClick={download}
                  className="rounded-btn border border-moss-border bg-transparent px-[18px] py-3 text-[13.5px] font-medium text-moss"
                >
                  Download my consent record
                </button>
              </div>
            </div>
          )}

          {/* Footer nav (steps 1–5) */}
          {wiz.showNav && (
            <div className="mt-8 flex flex-wrap items-center gap-4 border-t border-line-row pt-5">
              <p className="min-w-0 flex-1 text-[12.5px] leading-relaxed text-ink-faint">{effectiveHint(step)}</p>
              {wiz.showBack && (
                <button
                  type="button"
                  onClick={wiz.back}
                  disabled={submitting}
                  className="rounded-btn border border-line px-[18px] py-3 text-[13.5px] font-medium text-ink-muted hover:border-terra hover:text-terra-deep"
                >
                  Back
                </button>
              )}
              <GatedButton gated={submitting || !effectiveGateOk(step)} hint={effectiveHint(step)} onClick={handleNext}>
                {submitting ? 'Working…' : completing && step === 5 ? 'Save profile draft' : wiz.nextLabel}
              </GatedButton>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Step 1 ───────────────────────────────────────────────────────────────────
function StepAccount({
  wiz,
  password,
  confirm,
  setPassword,
  setConfirm,
  guardianPin,
  setGuardianPin,
  relOptions,
  existingAccount,
  needsPin,
}: {
  wiz: ReturnType<typeof useSignupWizard>
  password: string
  confirm: string
  setPassword: (v: string) => void
  setConfirm: (v: string) => void
  guardianPin: string
  setGuardianPin: (v: string) => void
  relOptions: Array<{ value: Relationship; label: string }>
  existingAccount: boolean
  needsPin: boolean
}) {
  return (
    <div>
      <h1 className="font-serif text-[30px] font-medium tracking-[-.015em] text-ink">First, you.</h1>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {[
          { key: 'family' as const, label: 'For my family', body: 'I am setting up profiles for my children, and managing what they can do.' },
          { key: 'solo' as const, label: 'Just for me', body: 'I am an adult joining on my own. No child profiles, no family section.' },
        ].map((m) => {
          const on = wiz.draft.mode === m.key
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => wiz.setMode(m.key)}
              aria-pressed={on}
              className={cn(
                'rounded-input border p-4 text-left transition-colors',
                on ? 'border-terra bg-paper-panel' : 'border-line-soft bg-transparent',
              )}
            >
              <div className="font-serif text-[16px] text-ink">{m.label}</div>
              <div className="mt-1 text-[12.5px] leading-relaxed text-ink-muted">{m.body}</div>
            </button>
          )
        })}
      </div>

      <div className="mt-6 grid gap-4">
        <Input label="Full legal name" value={wiz.draft.name} onChange={(e) => wiz.setName(e.target.value)} placeholder="e.g. Dana Rowe" />
        <Input label="Email" type="email" value={wiz.draft.email} onChange={(e) => wiz.setEmail(e.target.value)} placeholder="you@example.com" />
        {!existingAccount && <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" />
          <Input label="Confirm password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat it" />
        </div>}
        <Input label="Your date of birth" type="date" value={wiz.draft.dob} onChange={(e) => wiz.setDob(e.target.value)} />
        {wiz.relShown && (
          <Select label="Your relationship to the children" value={wiz.draft.rel} onChange={(e) => wiz.setRel(e.target.value as Relationship)}>
            {relOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        )}
        {!wiz.solo && needsPin && (
          <div>
            <Input
              label="Family guardian PIN (4–8 digits)"
              inputMode="numeric"
              value={guardianPin}
              onChange={(e) => setGuardianPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder="e.g. 4821"
              className="font-mono tracking-[.2em]"
            />
            <p className="mt-[6px] text-[12px] leading-relaxed text-ink-muted">
              A grown-up enters this to switch to an adult profile. Kids can switch to their own profile freely.
            </p>
          </div>
        )}
      </div>

      <div className={cn('mt-5 rounded-input px-4 py-3 text-[12.5px] leading-relaxed', toneBox(wiz.ageTone))}>{wiz.ageNote}</div>
    </div>
  )
}

// ── Step 2 ───────────────────────────────────────────────────────────────────
function StepNotice({ wiz }: { wiz: ReturnType<typeof useSignupWizard> }) {
  // Catalog kinds (and short test aliases) for the combined legal ack checkbox.
  const LEGAL_ACK_KINDS = new Set([
    'terms_of_service',
    'privacy_policy',
    'signup_data_care',
    'terms',
    'privacy',
    'dataCare',
  ])
  const legalAcks = wiz.ackRequirements.filter((a) => LEGAL_ACK_KINDS.has(a.kind))
  const otherAcks = wiz.ackRequirements.filter((a) => !LEGAL_ACK_KINDS.has(a.kind))
  const allLegalAcksAccepted = legalAcks.length > 0 && legalAcks.every((a) => wiz.ackAccepted(a.kind))

  const toggleAllLegalAcks = (checked: boolean) => {
    wiz.setLegalAcks(
      legalAcks.map((a) => a.kind),
      checked,
    )
  }

  const legalAckLabel = (a: (typeof legalAcks)[number]) =>
    a.title || a.text || humanizeAckKind(a.kind)

  return (
    <div>
      <h1 className="font-serif text-[30px] font-medium tracking-[-.015em] text-ink">What we collect, and what we don&rsquo;t.</h1>
      <p className="mt-3 max-w-[64ch] text-[13px] leading-relaxed text-ink-muted">{wiz.noticeLede}</p>

      {wiz.acknowledgementState === 'loading' && <p role="status" className="mt-4 text-sm text-ink-muted">Loading current agreements…</p>}
      {wiz.acknowledgementState === 'error' && <div role="alert" className="mt-4 rounded-input bg-honey-bg p-4 text-sm text-honey-fg">We could not load the current agreements. Please try again before continuing.<button type="button" className="ml-2 underline" onClick={wiz.retryAcknowledgements}>Try again</button></div>}
      {(legalAcks.length > 0 || otherAcks.length > 0) && (
        <div className="mt-5">
          <div className="mb-2 font-mono text-[10.5px] uppercase tracking-label text-ink-faint">Your agreements</div>
          <div className="grid gap-2">
            {/* Consolidated legal acknowledgements checkbox — covers all three legal docs */}
            {legalAcks.length > 0 && (
              <CheckRow checked={allLegalAcksAccepted} onToggle={() => toggleAllLegalAcks(!allLegalAcksAccepted)}>
                I accept the{' '}
                {legalAcks.map((a, idx) => (
                  <span key={a.kind}>
                    {a.url ? (
                      <a href={a.url} target="_blank" rel="noreferrer" className="text-terra-deep underline">
                        {legalAckLabel(a)}
                      </a>
                    ) : (
                      <span>{legalAckLabel(a)}</span>
                    )}
                    {idx < legalAcks.length - 1 && (idx === legalAcks.length - 2 ? ', and the ' : ', the ')}
                  </span>
                ))}
                .
              </CheckRow>
            )}

            {/* Other (non-legal) acknowledgements rendered individually */}
            {otherAcks.map((a) => (
              <CheckRow key={a.kind} checked={wiz.ackAccepted(a.kind)} onToggle={() => wiz.toggleAck(a.kind)}>
                {/*
                  Non-legal acknowledgements: render each as-is with title + url,
                  or fallback text / humanized kind name.
                */}
                {a.title ? (
                  <>
                    I accept the{' '}
                    {a.url ? (
                      <a href={a.url} target="_blank" rel="noreferrer" className="text-terra-deep underline">
                        {a.title}
                      </a>
                    ) : (
                      <span>{a.title}</span>
                    )}
                    .
                  </>
                ) : a.text ? (
                  <span>{a.text}</span>
                ) : (
                  <span>I accept the {humanizeAckKind(a.kind)}.</span>
                )}
              </CheckRow>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {wiz.noticeCards.map((c) => (
          <div key={c.title} className="rounded-input border border-line-soft bg-paper-surface p-4">
            <span className={cn('inline-block rounded-pill px-2 py-[3px] font-mono text-[9.5px] uppercase tracking-label', pillClasses(c.pill))}>{c.pill}</span>
            <div className="mt-2 font-serif text-[15px] text-ink">{c.title}</div>
            <div className="mt-1 text-[12.5px] leading-relaxed text-ink-muted">{c.body}</div>
          </div>
        ))}
      </div>

      <div className="mt-5">
        <CheckRow checked={wiz.draft.notice} onToggle={wiz.toggleNotice}>
          {wiz.consentTick}
        </CheckRow>
      </div>
    </div>
  )
}

// ── Step 3 ───────────────────────────────────────────────────────────────────
function StepProfiles({
  wiz,
  supOptions,
  onFlash,
}: {
  wiz: ReturnType<typeof useSignupWizard>
  supOptions: Array<{ value: Supervision; label: string }>
  onFlash: (m: string) => void
}) {
  return (
    <div>
      <h1 className="font-serif text-[30px] font-medium tracking-[-.015em] text-ink">{wiz.step3Title}</h1>
      <p className="mt-3 max-w-[64ch] text-[13px] leading-relaxed text-ink-muted">{wiz.step3Lede}</p>

      {wiz.solo ? (
        <div className="mt-5 max-w-[360px]">
          <Input
            label="Your handle"
            value={wiz.draft.handle}
            onChange={(e) => wiz.setHandle(e.target.value)}
            placeholder="e.g. willow_fox42"
            className="font-mono"
          />
        </div>
      ) : (
        <div className="mt-5 grid gap-4">
          {wiz.draft.kids.map((k, i) => {
            const note = wiz.kidNote(i)
            const age = ageFromDob(k.dob)
            const under13 = age !== null && age < 13
            return (
              <div key={i} className="rounded-input border border-line-soft bg-paper-surface p-4">
                <div className="mb-3 flex items-center">
                  <span className="font-mono text-[10.5px] uppercase tracking-label text-ink-faint">Person {i + 1}</span>
                  {wiz.draft.kids.length > 1 && (
                    <button
                      type="button"
                      onClick={() => wiz.removeKid(i)}
                      className="ml-auto text-[12px] text-terra-deep hover:text-terra-hover"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input label="First name" id={`kid-${i}-first`} value={k.first} onChange={(e) => wiz.setKidFirst(i, e.target.value)} placeholder="First name" />
                  <Input label="Date of birth" id={`kid-${i}-dob`} type="date" value={k.dob} onChange={(e) => wiz.setKidDob(i, e.target.value)} />
                </div>
                <div className="mt-3">
                  <Select
                    label="Supervision"
                    id={`kid-${i}-supervision`}
                    value={k.supervision}
                    onChange={(e) => {
                      const v = e.target.value as Supervision
                      if (wiz.setKidSupervision(i, v) === 'locked') {
                        onFlash('Under 13 must have a parent approve everything.')
                      }
                    }}
                  >
                    {supOptions.map((o) => (
                      <option key={o.value} value={o.value} disabled={o.value === 'self' && under13}>
                        {o.label}
                        {o.value === 'self' && under13 ? ` — Locked, they are ${age}.` : ''}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className={cn('mt-3 rounded-input px-3 py-2 text-[12px] leading-relaxed', toneBox(note.tone))}>{note.text}</div>
              </div>
            )
          })}
          <button
            type="button"
            onClick={wiz.addKid}
            className="rounded-btn border border-dashed border-line px-4 py-3 text-[13px] text-ink-muted hover:border-terra hover:text-terra-deep"
          >
            + Add another person
          </button>
        </div>
      )}
    </div>
  )
}

// ── Step 4 ───────────────────────────────────────────────────────────────────
function StepConsent({
  wiz,
  title,
  intro,
  sigIntro,
}: {
  wiz: ReturnType<typeof useSignupWizard>
  title: string
  intro: string
  sigIntro: string
}) {
  return (
    <div>
      <h1 className="font-serif text-[30px] font-medium tracking-[-.015em] text-ink">{title}</h1>
      <p className="mt-3 max-w-[64ch] text-[13px] leading-relaxed text-ink-muted">{intro}</p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {VERIFICATION_METHODS.map((m) => {
          const on = wiz.draft.method === m.key
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => wiz.setMethod(m.key)}
              disabled={!m.available}
              aria-pressed={on}
              className={cn('rounded-input border p-4 text-left transition-colors disabled:opacity-60', on ? 'border-terra bg-paper-panel' : 'border-line-soft bg-transparent')}
            >
              <div className="font-serif text-[15px] text-ink">{m.label}</div>
              <div className="mt-1 text-[12.5px] leading-relaxed text-ink-muted">{m.body}</div>
            </button>
          )
        })}
      </div>

      <p className="mt-4 rounded-input bg-paper-panel p-4 text-[13px] leading-relaxed text-ink-muted">Payment verification is pending. After you create your account, continue to secure checkout. Only confirmed payment evidence can complete verification.</p>

      {!wiz.solo && (
        <div className="mt-6 grid gap-2">
          {wiz.draft.kids.map((_, i) => (
            <CheckRow key={i} checked={wiz.draft.kids[i].consent} onToggle={() => wiz.toggleKidConsent(i)}>
              {wiz.kidConsentText(i)}
            </CheckRow>
          ))}
        </div>
      )}

      <div className="mt-6">
        <p className="mb-2 max-w-[64ch] text-[12.5px] leading-relaxed text-ink-muted">{sigIntro}</p>
        <input
          value={wiz.draft.sig}
          onChange={(e) => wiz.setSig(e.target.value)}
          placeholder={wiz.draft.name.trim() || 'Your full legal name'}
          className={cn(
            'w-full rounded-input border bg-paper-surface px-4 py-3 font-serif text-[19px] italic text-ink outline-none',
            wiz.sigStatus.tone === 'warn' ? 'border-[#c98f5c]' : 'border-line focus:border-terra',
          )}
        />
        <div
          className={cn(
            'mt-2 text-[12px]',
            wiz.sigStatus.tone === 'positive' ? 'text-moss' : wiz.sigStatus.tone === 'warn' ? 'text-honey-fg' : 'text-ink-faint',
          )}
        >
          {wiz.sigStatus.text}
        </div>
      </div>
    </div>
  )
}

// ── Step 5 ───────────────────────────────────────────────────────────────────
function StepReview({ wiz, withdrawNote }: { wiz: ReturnType<typeof useSignupWizard>; withdrawNote: string }) {
  return (
    <div>
      <h1 className="font-serif text-[30px] font-medium tracking-[-.015em] text-ink">Check it over.</h1>
      <div className="mt-5 overflow-hidden rounded-input border border-line-soft">
        {wiz.review.map((row, i) => (
          <div key={row.label} className={cn('grid grid-cols-[160px_1fr] gap-4 px-4 py-3', i % 2 === 1 && 'bg-paper-row')}>
            <div className="font-mono text-[10.5px] uppercase tracking-label text-ink-faint">{row.label}</div>
            <div className="text-[13px] leading-relaxed text-ink">{row.value}</div>
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-input bg-honey-bg px-4 py-3 text-[12.5px] leading-relaxed text-honey-fg">{withdrawNote}</div>
    </div>
  )
}
